import React, { useRef, useState } from "react";
import { Camera, Video, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { photoSrc } from "@/components/Stars";
import { MAX_VIDEO_SECONDS, MAX_VIDEO_BYTES, MAX_PHOTO_BYTES, VIDEO_TYPES, videoDuration } from "@/lib/media";

const ACCEPT = ["image/jpeg", "image/png", "image/webp", ...VIDEO_TYPES].join(",");

/** One slot: a photo OR a video. `media` = { kind, file?, preview?, url?, poster? } */
export default function MediaPicker({ media, setMedia, progress }) {
  const { t } = useLang();
  const fileRef = useRef(null);
  const [error, setError] = useState("");

  const pick = async (file) => {
    setError("");
    const isVideo = VIDEO_TYPES.includes(file.type);
    if (!isVideo && !file.type.startsWith("image/")) return;
    if (isVideo && file.size > MAX_VIDEO_BYTES) { setError(t("review_video_too_big")); return; }
    if (!isVideo && file.size > MAX_PHOTO_BYTES) { setError(t("review_photo_too_big")); return; }
    if (isVideo) {
      const d = await videoDuration(file);
      if (d > MAX_VIDEO_SECONDS + 0.5) { setError(t("review_video_too_long")); return; }
    }
    setMedia({ kind: isVideo ? "video" : "photo", file, preview: URL.createObjectURL(file) });
  };

  const uploading = progress > 0 && progress < 100;

  return (
    <div className="space-y-1.5" data-testid="review-media-picker">
      <div className="flex items-center gap-3">
        <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="review-media-input"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ""; }} />
        {media ? (
          <div className="relative">
            {media.kind === "video" ? (
              <video src={media.preview || photoSrc(media.url)} poster={media.poster ? photoSrc(media.poster) : undefined} muted playsInline preload="metadata"
                className="w-24 h-16 object-cover rounded-lg border border-stoneLine bg-ink" data-testid="review-video-preview" />
            ) : (
              <img src={media.preview || photoSrc(media.url)} alt="" className="w-16 h-16 object-cover rounded-lg border border-stoneLine" data-testid="review-photo-preview" />
            )}
            {uploading && (
              <div className="absolute inset-0 rounded-lg bg-ink/70 flex items-center justify-center text-[10px] font-semibold text-sand" data-testid="review-upload-progress">
                {progress}%
              </div>
            )}
            {!uploading && (
              <button type="button" onClick={() => setMedia(null)} aria-label={t("review_photo_remove")} className="absolute -top-2 -end-2 w-5 h-5 rounded-full bg-ink text-sand flex items-center justify-center" data-testid="review-media-remove"><X className="w-3 h-3" /></button>
            )}
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="review-add-media"
            className="flex-1 flex items-center gap-3 rounded-xl border-2 border-dashed border-ochre/50 bg-ochre/5 hover:bg-ochre/10 hover:border-ochre px-3 py-2.5 text-start transition-colors">
            <span className="flex items-center gap-1 text-ochre"><Camera className="w-5 h-5" /><Video className="w-5 h-5" /></span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink">{t("media_pick")}</span>
              <span className="block text-[10px] text-muted-foreground leading-tight">{t("review_media_hint")}</span>
            </span>
          </button>
        )}
        {media && <span className="text-[10px] text-muted-foreground leading-tight">{t("review_media_hint")}</span>}
      </div>
      {error && <p className="text-xs text-red-600" data-testid="review-media-error">{error}</p>}
    </div>
  );
}
